import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { Spinner } from './Spinner';
import type { ButtonStyleProps } from './styles';
import * as S from './styles';

type BasicButtonAttributes = ButtonHTMLAttributes<HTMLButtonElement>;
type BasicAnchorAttributes = AnchorHTMLAttributes<HTMLAnchorElement>;

export type ButtonProps =
  | (BasicButtonAttributes &
      Partial<ButtonStyleProps> & { children?: React.ReactNode } & {
        as?: 'button';
      })
  | (BasicAnchorAttributes &
      Partial<ButtonStyleProps> & { children?: React.ReactNode } & {
        as: 'a';
        disabled?: never;
      });

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      isLoading = false,
      colorScheme = 'blue',
      variant = 'fill',
      size = 'medium',
      disabled,
      leftIcon,
      rightIcon,
      as = 'button',
      ...props
    }: ButtonProps,
    ref
  ) => {
    const Element: React.ElementType = as === 'button' ? S.ButtonRoot : S.AnchorRoot;

    return (
      <Element
        disabled={disabled || isLoading}
        isLoading={isLoading}
        variant={variant}
        size={size}
        colorScheme={colorScheme}
        ref={ref}
        {...props}
      >
        <S.ContentWrapper data-testid="buttonContentWrapper" isLoading={isLoading}>
          {leftIcon ? <S.LeftIconWrapper size={size}>{leftIcon}</S.LeftIconWrapper> : null}
          {children}
          {rightIcon ? <S.RightIconWrapper size={size}>{rightIcon}</S.RightIconWrapper> : null}
        </S.ContentWrapper>
        {isLoading && <Spinner role="spinner" colorScheme={colorScheme} variant={variant} size={14} />}
      </Element>
    );
  }
);
export default Button;
